import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import InputMask from 'react-input-mask';
import { Button, Checkbox, Form, Modal } from 'semantic-ui-react';

import { useDidUpdate, useToggle } from '../../lib/hooks';
import { Input } from '../../lib/custom-ui';
import { useForm } from '../../hooks';

import styles from './CardReminderStep.module.scss';

const CardReminderStep = React.memo(({ defaultValue, defaultEnabled, onApply, onClose }) => {
  const [t] = useTranslation();
  const datePickerDateFormat = t('format:datePickerDate', { defaultValue: 'yyyy.MM.dd' });
  const datePickerDateTimeFormat = t('format:datePickerDateTime', {
    defaultValue: 'yyyy.MM.dd HH:mm',
  });

  const [enabled, setEnabled] = useState(Boolean(defaultEnabled));

  const [data, handleFieldChange, setData] = useForm(() => {
    const date = defaultValue || new Date();
    return {
      date: t('format:datePickerDate', {
        defaultValue: datePickerDateFormat,
        postProcess: 'formatDate',
        value: date,
      }),
      time: t('format:time', {
        postProcess: 'formatDate',
        value: date,
      }),
    };
  });

  const handleFieldChangeAndEnable = useCallback(
    (event, field) => {
      handleFieldChange(event, field);
      setEnabled(true);
    },
    [handleFieldChange],
  );

  const [selectTimeFieldState, selectTimeField] = useToggle();

  const dateField = useRef(null);
  const timeField = useRef(null);

  const nullableDate = useMemo(() => {
    const date = t('format:datePickerDate', {
      defaultValue: datePickerDateFormat,
      postProcess: 'parseDate',
      value: data.date,
    });

    if (!Number.isNaN(date.getTime())) {
      return date;
    }

    const fallbackDate = t('format:date', {
      postProcess: 'parseDate',
      value: data.date,
    });

    if (Number.isNaN(fallbackDate.getTime())) {
      return null;
    }

    return fallbackDate;
  }, [data.date, t, datePickerDateFormat]);

  const handleDatePickerChange = useCallback(
    (date) => {
      setData((prevData) => ({
        ...prevData,
        date: t('format:datePickerDate', {
          defaultValue: datePickerDateFormat,
          postProcess: 'formatDate',
          value: date,
        }),
      }));
      setEnabled(true);
      selectTimeField();
    },
    [setData, selectTimeField, t, datePickerDateFormat],
  );

  const handleSubmit = useCallback(() => {
    if (!enabled) {
      onApply({
        date: defaultValue || null,
        enabled: false,
      });
      onClose();
      return;
    }

    if (!nullableDate) {
      dateField.current.select();
      return;
    }

    const value = t('format:datePickerDateTime', {
      defaultValue: datePickerDateTimeFormat,
      postProcess: 'parseDate',
      value: `${data.date} ${data.time}`,
    });

    if (!Number.isNaN(value.getTime())) {
      onApply({
        date: value,
        enabled: true,
      });
      onClose();
      return;
    }

    const fallbackValue = t('format:dateTime', {
      postProcess: 'parseDate',
      value: `${data.date} ${data.time}`,
    });

    if (Number.isNaN(fallbackValue.getTime())) {
      timeField.current.select();
      return;
    }

    onApply({
      date: fallbackValue,
      enabled: true,
    });
    onClose();
  }, [enabled, defaultValue, nullableDate, onApply, onClose, data, t, datePickerDateTimeFormat]);

  const handleClearClick = useCallback(() => {
    onApply({
      date: null,
      enabled: false,
    });
    onClose();
  }, [onApply, onClose]);

  useEffect(() => {
    dateField.current.select();
  }, []);

  useDidUpdate(() => {
    if (timeField.current) {
      timeField.current.select();
    }
  }, [selectTimeFieldState]);

  return (
    <>
      <Modal.Header>Напоминание</Modal.Header>
      <Modal.Content>
        <div className={styles.controlsRow}>
          <Checkbox
            checked={enabled}
            label="Активировать"
            onChange={() => setEnabled((prev) => !prev)}
          />
        </div>
        <Form onSubmit={handleSubmit}>
          <div className={styles.fieldWrapper}>
            <div className={styles.fieldBox}>
              <div className={styles.text}>{t('common.date')}</div>
              <Input
                ref={dateField}
                name="date"
                value={data.date}
                onChange={handleFieldChangeAndEnable}
              />
            </div>
            <div className={styles.fieldBox}>
              <div className={styles.text}>{t('common.time')}</div>
              <InputMask
                mask="99:99"
                placeholder="__:__"
                value={data.time}
                onChange={(e) =>
                  handleFieldChangeAndEnable(null, { name: 'time', value: e.target.value })
                }
              >
                {(inputProps) => <Input {...inputProps} />}
              </InputMask>
            </div>
          </div>
          <DatePicker
            inline
            disabledKeyboardNavigation
            selected={nullableDate}
            onChange={handleDatePickerChange}
          />
        </Form>
        <div className={styles.buttonsRow}>
          <Button type="button" positive content="Применить" onClick={handleSubmit} />
          <Button type="button" negative content="Удалить" onClick={handleClearClick} />
        </div>
      </Modal.Content>
    </>
  );
});

CardReminderStep.propTypes = {
  defaultValue: PropTypes.instanceOf(Date),
  defaultEnabled: PropTypes.bool,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

CardReminderStep.defaultProps = {
  defaultValue: undefined,
  defaultEnabled: false,
};

export default CardReminderStep;

